import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";

/**
 * useAttendanceData
 * Fetches today's attendance records AND previous month records from the
 * no-payroll-sync visibility endpoint (read-only: same data as the regular
 * visibility endpoint, but never writes to payroll).
 * The previous month data enables accurate trend / comparison analysis
 * instead of using hardcoded values.
 */
const useAttendanceData = ({ BASE_URL, language }) => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [previousMonthData, setPreviousMonthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        setLoading(true);
        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];

        // Determine a representative day from previous month
        const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 15);
        const prevMonthStr = prevMonth.toISOString().split("T")[0];

        const viewType =
          localStorage.getItem("managerViewType") ||
          localStorage.getItem("viewType") ||
          "admin";

        // Fetch today's attendance — uses the no-payroll-sync endpoint since
        // this widget only reads status/checkIn/checkOut and must not
        // trigger payroll writes.
        const todayUrl = new URL(`${BASE_URL}/attendance/visibility-no-payroll-sync`);
        todayUrl.searchParams.append("date", todayStr);

        // Fetch previous month attendance for comparison
        const prevUrl = new URL(`${BASE_URL}/attendance/visibility-no-payroll-sync`);
        prevUrl.searchParams.append("date", prevMonthStr);

        const [todayResp, prevResp] = await Promise.all([
          apiFetch(todayUrl.toString(), {
            method: "GET",
            headers: {
              "Accept-Language": language || "en",
              "x-view-type": viewType,
            },
          }),
          apiFetch(prevUrl.toString(), {
            method: "GET",
            headers: {
              "Accept-Language": language || "en",
              "x-view-type": viewType,
            },
          }).catch(() => null), // Don't fail if prev month data is unavailable
        ]);

        // Process today's data
        if (!todayResp.ok) {
          console.warn(`Attendance fetch failed: ${todayResp.status}`);
          setAttendanceData([]);
        } else {
          const json = await todayResp.json();
          const records = json?.data?.attendance || [];
          const processedRecords = records.map((record) => ({
            id: record.id,
            employeeId: record.employeeId || record.employee?.id,
            employeeName: record.employeeName || record.employee?.name,
            status: record.status || "-",
            checkIn: record.checkIn,
            checkOut: record.checkOut,
          }));
          setAttendanceData(processedRecords);
        }

        // Process previous month's data (for trend analysis)
        if (prevResp && prevResp.ok) {
          const prevJson = await prevResp.json();
          const prevRecords = prevJson?.data?.attendance || [];
          const processedPrev = prevRecords.map((record) => ({
            id: record.id,
            employeeId: record.employeeId || record.employee?.id,
            employeeName: record.employeeName || record.employee?.name,
            status: record.status || "-",
            checkIn: record.checkIn,
            checkOut: record.checkOut,
          }));
          setPreviousMonthData(processedPrev);
        } else {
          setPreviousMonthData([]);
        }

        setError(null);
      } catch (err) {
        console.error("Error fetching attendance data:", err);
        setError(err.message);
        setAttendanceData([]);
        setPreviousMonthData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, [BASE_URL, language]);

  return { attendanceData, previousMonthData, loading, error };
};

export default useAttendanceData;
